import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  User as UserIcon,
  ArrowLeft,
  TrendingUp,
  Building2,
  Layers,
  Car,
  Utensils,
  Calendar,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Play,
  Heart,
  Lock,
} from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTransfer } from "@/hooks/useTransfer";
import { useOfferTransfer, useMyTransferOffers } from "@/hooks/useOffer";
import { useMySignedNdas } from "@/hooks/useNda";
import { useAuthStore } from "@/store/authStore";
import { can } from "@/utils/permissions";
import { Button } from "@/components/ui/button";
import { useToggleSaveListing } from "@/hooks/useSavedListings";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/misc";
import { Spinner, NdaSignModal, OfferModal, MediaGallery } from "@/components/common";
import { cn } from "@/lib/utils";
import type { CreateOfferRequest, TransferImage } from "@/types";
import { formatNumber } from "@/utils";

/* ─── helpers ─── */
function formatPrice(str: string, currencyUnit: string = "VND") {
  if (!str) return "0";
  const n = parseFloat(str);
  if (isNaN(n)) return "0";

  return formatNumber(n) + " " + currencyUnit;
}

function getImages(images: TransferImage[]) {
  if (!images?.length) return [];
  return images.map((i) => ({
    url: i.image.publicUrl,
    type: i.type,
    isPrimary: i.isPrimary,
  }));
}

const offerSchema = z.object({
  message: z.string().min(10, "Tối thiểu 10 ký tự"),
});
type OfferData = z.infer<typeof offerSchema>;

/* ─── Component ─── */
export default function TransferDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const { data: item, isLoading, error, refetch } = useTransfer(id!);
  const isNdaError = error && (error as any).response?.data?.message === "Please Sign NDA";
  const { mutate: createOffer, isPending } = useOfferTransfer();
  const { isSaved, toggleSave } = useToggleSaveListing();
  const saved = item ? isSaved(item.id) : false;
  const { data: myOffers } = useMyTransferOffers(user?.id);
  const isOffered = Array.isArray(myOffers) ? myOffers.some((o: any) => o.entityId === item?.id) : false;

  const { data: mySignedNdas } = useMySignedNdas();
  const isSignedNda = Array.isArray(mySignedNdas) ? mySignedNdas.some((n: any) => n.entityId === item?.id && n.entityType === 'BusinessTransfer') : false;
  const ndaRequired = item?.ndaRequired === true;
  const needsToSignNda = ndaRequired && !isSignedNda;



  const [imgIdx, setImgIdx] = useState(0);
  const [offerOpen, setOfferOpen] = useState(false);
  const [ndaOpen, setNdaOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<OfferData>({
    resolver: zodResolver(offerSchema),
  });

  if (isLoading) return <Spinner />;

  if (isNdaError) {
    return (
      <div className="max-w-3xl mx-auto py-20 text-center animate-fade-in">
        <div className="bg-white rounded-2xl shadow-sm border p-10 max-w-lg mx-auto">
          <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock size={32} />
          </div>
          <h2 className="text-2xl font-bold mb-3 text-gray-800">Yêu cầu bảo mật thông tin</h2>
          <p className="text-gray-500 mb-8 leading-relaxed">
            Tin đăng này yêu cầu bạn phải ký Thỏa thuận bảo mật thông tin (NDA) trước khi xem chi tiết và trao đổi với người bán.
          </p>
          <Button
            onClick={() => setNdaOpen(true)}
            className="w-full btn-brand py-3 text-base mb-3"
          >
            Ký NDA để xem chi tiết
          </Button>
          <Link to="/sang-nhuong">
            <Button variant="ghost" className="w-full">
              Quay lại danh sách
            </Button>
          </Link>
        </div>

        {/* ══ NDA MODAL (For Error Screen) ══ */}
        <NdaSignModal
          isOpen={ndaOpen}
          onClose={() => setNdaOpen(false)}
          entityType="BusinessTransfer"
          entityId={id!}
          onSuccess={() => {
            setNdaOpen(false);
            refetch();
          }}
        />
      </div>
    );
  }

  if (!item)
    return (
      <div className="text-center py-20">
        <p style={{ color: "var(--text-3)" }}>Không tìm thấy tin này</p>
        <Link to="/sang-nhuong">
          <Button variant="ghost" className="mt-4">
            Quay lại danh sách
          </Button>
        </Link>
      </div>
    );

  const imgs = getImages(item.images);
  const onSubmit = (data: OfferData) => {
    const payload = {
      entityType: "BusinessTransfer",
      // entityType: "BusinessTransferRequest",
      entityId: item.id,
      receiverId: item.creator?.id || "",
      content: {
        title: item.title,
        terms: "Thỏa thuận trực tiếp",
      },
      message: data.message,
      status: "pending",
    };
    createOffer(payload as any, {
      onSuccess: () => {
        reset();
        setOfferOpen(false);
      },
    });
  };

  const fullAddr = [
    item.streetAddress,
    item.ward?.name,
    item.district?.name,
    item.province?.name,
    item.country?.name,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fade-in">
      {/* Breadcrumb */}
      <div className="flex items-center justify-between gap-4">
        <div
          className="flex items-center gap-2 text-sm"
          style={{ color: "var(--text-3)" }}
        >
          <Link
            to="/sang-nhuong"
            className="flex items-center gap-1 hover:text-orange-500 transition-colors"
          >
            <ArrowLeft size={14} /> Sang nhượng
          </Link>
          <span>/</span>
          <span className="truncate max-w-xs" style={{ color: "var(--text-1)" }}>
            {item.title}
          </span>
        </div>

        {user?.id === item.createdBy && (
          <Link to={`/sang-nhuong/sua/${item.id}`}>
            <Button variant="outline" size="sm" className="gap-2 font-bold shadow-sm h-9 border-orange-200 text-orange-600 hover:bg-orange-50 hover:text-orange-700">
              Chỉnh sửa tin đăng
            </Button>
          </Link>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* ── Left: Main content ── */}
        <div className="lg:col-span-2 space-y-5">
          {/* Image gallery */}
          <MediaGallery
            images={item.images}
            approvalStatus={item.approvalStatus}
            fallbackIcon="🏪"
          />

          {/* Title + price */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge variant="amber">{item.businessTypeName}</Badge>
                <Badge variant="secondary">{item.businessSectorName}</Badge>
              </div>
              <h1
                className="font-display font-bold text-2xl mb-4"
                style={{ color: "var(--text-1)" }}
              >
                {item.title}
              </h1>

              {/* Price block */}
              <div
                className="rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x border-orange-200/50"
                style={{
                  background: "var(--brand-pale)",
                  border: "1px solid rgba(249,115,22,0.2)",
                }}
              >
                <div className="flex flex-col">
                  <p
                    className="text-xs font-medium mb-1"
                    style={{ color: "var(--text-3)" }}
                  >
                    Giá sang nhượng
                  </p>
                  <p
                    className="font-display font-black text-xl truncate"
                    style={{ color: "var(--brand)" }}
                    title={formatPrice(item.price, item.currencyUnit)}
                  >
                    {formatPrice(item.price, item.currencyUnit)}
                  </p>
                </div>
                <div className="flex flex-col md:pl-6 pt-4 md:pt-0 border-orange-200/50">
                  <p
                    className="text-xs font-medium mb-1"
                    style={{ color: "var(--text-3)" }}
                  >
                    Doanh thu/tháng
                  </p>
                  <p
                    className="font-display font-bold text-lg truncate"
                    style={{ color: "var(--text-1)" }}
                    title={formatPrice(item.revenue, item.currencyUnit)}
                  >
                    {formatPrice(item.revenue, item.currencyUnit)}
                  </p>
                </div>
                <div className="flex flex-col md:pl-6 pt-4 md:pt-0 border-orange-200/50">
                  <p
                    className="text-xs font-medium mb-1"
                    style={{ color: "var(--text-3)" }}
                  >
                    Lợi nhuận/tháng
                  </p>
                  <p
                    className="font-display font-bold text-lg text-green-600 truncate"
                    title={formatPrice(item.profit, item.currencyUnit)}
                  >
                    {formatPrice(item.profit, item.currencyUnit)}
                  </p>
                </div>
              </div>

              <div
                className="flex items-center gap-1.5 text-sm"
                style={{ color: "var(--text-3)" }}
              >
                <MapPin size={14} /> {fullAddr}
              </div>
            </CardContent>
          </Card>

          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle>Mô tả chi tiết</CardTitle>
            </CardHeader>
            <CardContent>
              <p
                className="text-sm leading-relaxed whitespace-pre-wrap"
                style={{ color: "var(--text-2)" }}
              >
                {item.description}
              </p>
            </CardContent>
          </Card>

          {/* Property details */}
          <Card>
            <CardHeader>
              <CardTitle>Thông tin mặt bằng</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  {
                    icon: <Layers size={15} />,
                    label: "Diện tích",
                    value: item.area != null ? `${item.area} ${item.areaUnit || "m²"}` : null,
                  },
                  {
                    icon: <Building2 size={15} />,
                    label: "Số tầng",
                    value: item.floors != null ? `${item.floors} tầng` : null,
                  },
                  {
                    icon: <Utensils size={15} />,
                    label: "Sức chứa",
                    value: item.seatingCapacity != null ? `${item.seatingCapacity} chỗ` : null,
                  },
                  {
                    icon: <Car size={15} />,
                    label: "Chỗ đậu xe",
                    value: item.parkingSpaces != null ? `${item.parkingSpaces} xe` : null,
                  },
                  {
                    icon: <Layers size={15} />,
                    label: "Khu vực ngoài",
                    value: item.outsideSeatingArea != null ? `${item.outsideSeatingArea} m²` : null,
                  },
                  {
                    icon: <Layers size={15} />,
                    label: "Kho",
                    value: item.hasStorage ? (item.storageArea != null ? `${item.storageArea} m²` : "Có kho") : null,
                  },
                  {
                    icon: <Building2 size={15} />,
                    label: "Nhà vệ sinh",
                    value: item.toiletCount != null ? `${item.toiletCount} phòng` : null,
                  },
                  {
                    icon: <MapPin size={15} />,
                    label: "Cách trung tâm",
                    value: item.distanceToCityCenter != null ? `${item.distanceToCityCenter} km` : null,
                  },
                ].filter(i => i.value !== null).map(({ icon, label, value }) => (
                  <div key={label} className="flex items-start gap-2.5">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                      style={{
                        background: "var(--brand-dim)",
                        color: "var(--brand)",
                      }}
                    >
                      {icon}
                    </div>
                    <div>
                      <p
                        className="text-[11px] font-medium"
                        style={{ color: "var(--text-3)" }}
                      >
                        {label}
                      </p>
                      <p
                        className="text-sm font-semibold"
                        style={{ color: "var(--text-1)" }}
                      >
                        {value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Lease terms */}
          {item.leaseTerms && (
            <Card>
              <CardHeader>
                <CardTitle>Điều khoản thuê</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p style={{ color: "var(--text-2)" }}>{item.leaseTerms}</p>
                <div className="flex flex-wrap gap-4 mt-3">
                  <span
                    className="flex items-center gap-1.5"
                    style={{ color: "var(--text-3)" }}
                  >
                    <Calendar size={13} /> Thời hạn: {item.leaseDuration}{" "}
                    {item.leaseDurationUnit}
                  </span>
                  <span
                    className="flex items-center gap-1.5"
                    style={{ color: "var(--text-3)" }}
                  >
                    <Calendar size={13} /> Hết hạn:{" "}
                    {new Date(item.leaseExpiryDate).toLocaleDateString("vi-VN")}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Social media */}
          {(item.tiktokUrl || item.reelUrl || item.youtubeShortUrl) && (
            <Card>
              <CardHeader>
                <CardTitle>Video & Mạng xã hội</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-3">
                {item.tiktokUrl && (
                  <a
                    href={item.tiktokUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border hover:border-orange-400 hover:text-orange-500 transition-colors"
                    style={{
                      borderColor: "var(--border-c)",
                      color: "var(--text-2)",
                    }}
                  >
                    <Play size={14} /> TikTok <ExternalLink size={12} />
                  </a>
                )}
                {item.reelUrl && (
                  <a
                    href={item.reelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border hover:border-orange-400 hover:text-orange-500 transition-colors"
                    style={{
                      borderColor: "var(--border-c)",
                      color: "var(--text-2)",
                    }}
                  >
                    <Play size={14} /> Instagram Reel <ExternalLink size={12} />
                  </a>
                )}
                {item.youtubeShortUrl && (
                  <a
                    href={item.youtubeShortUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border hover:border-orange-400 hover:text-orange-500 transition-colors"
                    style={{
                      borderColor: "var(--border-c)",
                      color: "var(--text-2)",
                    }}
                  >
                    <Play size={14} /> YouTube Short <ExternalLink size={12} />
                  </a>
                )}
              </CardContent>
            </Card>
          )}


        </div>

        {/* ── Right: Sidebar ── */}
        <div className="space-y-4">
          {/* Contact */}
          <Card>
            <CardHeader>
              <CardTitle>Thông tin liên hệ</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: "var(--brand-dim)" }}
                >
                  <UserIcon size={16} style={{ color: "var(--brand)" }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: "var(--text-3)" }}>
                    Người đăng
                  </p>
                  <p
                    className="font-semibold text-sm"
                    style={{ color: "var(--text-1)" }}
                  >
                    {item.creator?.username}
                  </p>
                  <p className="text-xs" style={{ color: "var(--text-3)" }}>
                    {item.creator?.email}
                  </p>
                </div>
              </div>
              {can.sendOffer(user) && (
                <>
                  {needsToSignNda ? (
                    <button
                      onClick={() => setNdaOpen(true)}
                      className="w-full py-2.5 text-sm rounded-xl font-semibold flex items-center justify-center gap-1.5 transition-all btn-brand"
                    >
                      Ký NDA để Gửi đề nghị
                    </button>
                  ) : (
                    <button
                      onClick={() => setOfferOpen(true)}
                      disabled={isOffered}
                      className={cn(
                        "w-full py-2.5 text-sm rounded-xl font-semibold flex items-center justify-center gap-1.5 transition-all",
                        isOffered
                          ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                          : "btn-brand"
                      )}
                    >
                      {isOffered ? "Đã gửi đề nghị" : "Gửi đề nghị mua"}
                    </button>
                  )}
                </>
              )}
              {(!user || user.id !== item.createdBy) && (
                <button
                  onClick={() => toggleSave(item.id, "BusinessTransfer")}
                  className={cn(
                    "w-full py-2.5 text-sm rounded-xl font-semibold flex items-center justify-center gap-1.5 border transition-all mt-2",
                    saved
                      ? "bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                  )}
                >
                  <Heart size={14} className={cn(saved && "fill-orange-500 text-orange-500")} />
                  {saved ? "Đã lưu tin" : "Lưu tin"}
                </button>
              )}
            </CardContent>
          </Card>

          {/* Meta info */}
          <Card>
            <CardHeader>
              <CardTitle>Chi tiết</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-0">
                {[
                  ["Loại hình", item.businessTypeName],
                  ["Ngành", item.businessSectorName],
                  ["Tỉnh/TP", item.province?.name],
                  ["Quận/Huyện", item.district?.name],
                  ["Phường/Xã", item.ward?.name],
                  ["Quốc gia", item.country?.name],
                  [
                    "Trạng thái",
                    item.approvalStatus === "approved"
                      ? "✓ Đã duyệt"
                      : "⏳ Chờ duyệt",
                  ],
                  [
                    "Ngày đăng",
                    new Date(item.createdAt).toLocaleDateString("vi-VN"),
                  ],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex justify-between items-start py-2 border-b last:border-0 gap-2"
                    style={{ borderColor: "var(--border-c)" }}
                  >
                    <span
                      className="text-xs shrink-0"
                      style={{ color: "var(--text-3)" }}
                    >
                      {k}
                    </span>
                    <span
                      className="text-xs font-medium text-right"
                      style={{ color: "var(--text-1)" }}
                    >
                      {v ?? "—"}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Revenue/profit card */}
          <Card>
            <CardHeader>
              <CardTitle>Hiệu quả kinh doanh</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                {
                  label: "Giá sang nhượng",
                  value: formatPrice(item.price, item.currencyUnit),
                  color: "var(--brand)",
                },
                {
                  label: "Doanh thu/tháng",
                  value: formatPrice(item.revenue, item.currencyUnit),
                  color: "var(--text-1)",
                },
                {
                  label: "Lợi nhuận/tháng",
                  value: formatPrice(item.profit, item.currencyUnit),
                  color: "#16a34a",
                },
              ].map(({ label, value, color }) => (
                <div key={label} className="flex justify-between items-center">
                  <span className="text-xs" style={{ color: "var(--text-3)" }}>
                    {label}
                  </span>
                  <span className="text-sm font-bold" style={{ color }}>
                    {value}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
      {/* ══ OFFER MODAL ══ */}
      <OfferModal
        isOpen={offerOpen}
        onClose={() => setOfferOpen(false)}
        title="Gửi đề nghị mua"
        onSubmit={handleSubmit(onSubmit)}
        isPending={isPending}
      >
        <Textarea
          label="Lời nhắn *"
          placeholder="Giới thiệu bản thân và ý định mua..."
          rows={4}
          error={errors.message?.message}
          {...register("message")}
        />
      </OfferModal>

      {/* ══ NDA MODAL ══ */}
      <NdaSignModal
        isOpen={ndaOpen}
        onClose={() => setNdaOpen(false)}
        entityType="BusinessTransfer"
        entityId={item.id}
        description="Bạn cần ký Thỏa thuận bảo mật thông tin (NDA) để trao đổi với người bán."
        onSuccess={() => {
          setNdaOpen(false);
          setOfferOpen(true);
        }}
      />
    </div>
  );
}
